import Tooltip from 'components/Tooltip';
import React from 'react';
import { Row, Col, Card, Button } from 'reactstrap';

import { FilterConfig, BaseFilter, FilterGroupConfig } from './FilterTypes';

interface FilterMenuProps {
  filterConfigs: FilterConfig;
  onChange: () => void; // Callback for value changes
  onDone: () => void; // Callback for when the "Done" button is clicked
}

/**
 * FilterMenu component renders a list of filters based on the provided configuration.
 * Only filters that are enabled and configured to show in the filter menu are rendered.
 *
 * @param {FilterMenuProps} props - The props for the FilterMenu component.
 * @returns {JSX.Element} The rendered FilterMenu component.
 */
const FilterMenu: React.FC<FilterMenuProps> = ({
  filterConfigs,
  onChange,
  onDone,
}) => {
  return (
    <Card className="p-3 mb-0">
      <Row className="filter-menu-container">
        <Col lg="6" xl="5" className="left-panel">
          {renderPanelFilters(
            filterConfigs.leftPanelFilters,
            filterConfigs,
            onChange,
          )}
        </Col>
        <Col lg="6" xl="7" className="right-panel">
          {renderPanelFilters(
            filterConfigs.rightPanelFilters,
            filterConfigs,
            onChange,
          )}
        </Col>
      </Row>
      <Row className="filter-menu-footer pt-2 pb-0 border-top">
        <Col className="text-right">
          <Button color="primary" size="sm" onClick={onDone}>
            <i className="fas fa-clipboard-check mr-2"></i>
            Done
          </Button>
        </Col>
      </Row>
    </Card>
  );
};

/**
 * Renders the filters for a given panel.
 *
 * @param {string[]} panelFilters - The list of filter IDs for the panel.
 * @param {FilterConfig} filterConfigs - The filter configuration object.
 * @param {() => void} onChange - Callback for value changes.
 * @returns {JSX.Element[]} The rendered filters for the panel.
 */
const renderPanelFilters = (
  panelFilters: string[],
  filterConfigs: FilterConfig,
  onChange: () => void,
) => {
  return panelFilters
    .map((filterId) => {
      const filter = filterConfigs.filters.find(
        (f) => f.id === filterId && f.enabled && f.showInFilterMenu,
      );
      if (!filter) return null;

      if (filter instanceof FilterGroupConfig) {
        return (
          <div key={filter.id} className="filter-group mb-2">
            {renderFilterHeader(filter)}
            <div
              className="d-flex flex-wrap flex-fill
               filter-group-content"
              style={{ gap: '5px' }}
            >
              {filter.filters
                .filter((f) => f.enabled)
                .map((subFilter) => (
                  <div
                    className="d-flex flex-row flex-grow-1"
                    // style={{ flexBasis: '45%' }}
                    key={subFilter.id}
                  >
                    {subFilter.render(onChange)}
                  </div>
                ))}
            </div>
          </div>
        );
      } else if (filter instanceof BaseFilter) {
        return (
          <div key={filter.id} className="filter-item mb-2">
            {renderFilterHeader(filter)}
            {filter.render(onChange)}
          </div>
        );
      }
      return null;
    })
    .filter((element) => element !== null);
};

/**
 * Renders the header for a filter.
 *
 * @param {BaseFilter | FilterGroupConfig} filter - The filter for which to render the header.
 * @returns {JSX.Element} The rendered filter header.
 */
const renderFilterHeader = (filter: BaseFilter | FilterGroupConfig) => (
  <div className="filter-header text-uppercase fw-bold mb-2 d-flex align-items-center justify-content-between">
    <div className="d-flex align-items-center">
      {filter.iconClass && <i className={`${filter.iconClass} mr-2`}></i>}
      <span>{filter.label}</span>
    </div>
    {filter.tooltip && (
      <Tooltip title={filter.label} tooltip={filter.tooltip}>
        <i className="fas fa-info-circle ml-2"></i>
      </Tooltip>
    )}
  </div>
);

/**
 * Renders the content for a filter.
 *
 * @param {BaseFilter} filter - The filter for which to render the content.
 * @returns {JSX.Element} The rendered filter content.
 */
const renderFilterContent = (filter: BaseFilter, onChange) => (
  <div key={filter.id} className="filter-item">
    {filter.render(onChange)}
  </div>
);

export default FilterMenu;
